import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { numberLocale } from '@/lib/language';
import { useEnforcement } from '@/hooks/useEnforcement';
import { planTierLabel } from '@/lib/plans';
import { trackConversion } from '@/lib/analytics';
import { UpgradeModal } from '@/components/enforcement/UpgradeModal';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export function UsageIndicator() {
  const { t, i18n } = useTranslation('enforcement');
  const { phase, usagePct, count, limit, plan, totalProducts } = useEnforcement();
  const [showUpgrade, setShowUpgrade] = useState(false);

  // UX-Audit SEA-A3-17: mehr Produkte in den Feeds als Plaetze ist in GREEN
  // ein Hinweis, kein Alarm. Der Ring zeigt die echte Belegung, kein "!".
  const feedsExceed = totalProducts > limit && phase === 'GREEN';
  if (phase === 'GREEN' && !feedsExceed) return null;

  const size = 28;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(usagePct, 0) / 100, 1);
  const strokeDashoffset = circumference * (1 - progress);

  const color = phase === 'RED'
    ? 'hsl(var(--destructive))'
    : phase === 'YELLOW'
    ? 'hsl(var(--warning))'
    : 'hsl(var(--primary))';

  const displayValue = Math.round(usagePct);
  const nf = (n: number) => n.toLocaleString(numberLocale(i18n.language));

  // Eine Quelle fuer Tooltip UND `aria-label`.
  const statusText = feedsExceed
    ? t('indicator.feedsExceed', { formatted: nf(count), limit: nf(limit), total: nf(totalProducts) })
    : t('indicator.usage', { formatted: nf(count), limit: nf(limit), plan: planTierLabel(plan, i18n?.language, t) });

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => {
              // Funnel-Event (Bericht 8.3 Punkt 6): Klick auf den Ring im Header.
              trackConversion('usage_indicator_clicked', { phase });
              setShowUpgrade(true);
            }}
            aria-label={statusText}
            className="inline-flex items-center gap-1.5 h-11 sm:h-8 px-2 rounded-md hover:bg-accent transition-colors"
          >
            <span className="relative flex items-center justify-center h-7 w-7 shrink-0">
              <svg width={size} height={size} className="-rotate-90">
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="hsl(var(--border))"
                  strokeWidth={strokeWidth}
                />
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-500"
                />
              </svg>
              <span className="absolute text-[9px] font-bold" style={{ color }}>
                {displayValue}
              </span>
            </span>
            {/*
              Rang 3 der Reihenfolge des Weglassens in der 56-px-Leiste
              (siehe die Bilanz-Tabelle in `context/FeedSwitcher.tsx`): das
              sichtbare Wort kostet 51,1 px plus 6 px Abstand. Unter `xs`
              (480 px) sind das genau die 57 px, die der Kontext-Pille sonst
              fehlen -- bei 320 px blieben mit dem Wort 34,9 px Band fuer
              die Pille uebrig, sie malte dann 61 px in diese Knopfgruppe
              hinein (gemessen). Der RING bleibt in jeder Breite stehen, und mit
              ihm der zugaengliche Name: der haengt am `aria-label` des
              Knopfes (`statusText` oben), nicht an diesem Span. T2 verlangt
              ein Wort statt eines nackten `button "!"` -- das bleibt ab
              480 px erfuellt, darunter traegt es der Name allein.
            */}
            <span className="hidden xs:inline text-xs font-medium">{t('indicator.label')}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">
            {/* `planTierLabel` statt des rohen Tiers: "cap1000" ist kein
                Anzeigename — Baender heissen "1.000 Produkte". */}
            {statusText}
          </p>
        </TooltipContent>
      </Tooltip>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
    </>
  );
}
